"use client";

import { Suspense, useState, useEffect } from "react";
import { SignIn } from "@clerk/nextjs";
import { dark } from "@clerk/themes";
import { useSearchParams } from "next/navigation";
import AnimatedCharactersLoginPage from "@/components/ui/animated-characters-login-page";

function SignInContent() {
  const searchParams = useSearchParams();
  const reason = searchParams.get("reason");
  const [mounted, setMounted] = useState(false);

  // Clerk's <SignIn> component injects client-only DOM attributes (data-clerk-component)
  // that cause hydration mismatches. Defer rendering until after mount.
  useEffect(() => {
    setMounted(true);
  }, []);

  const getMessage = () => {
    if (reason === "timeout") {
      return "Your session expired due to inactivity. Please sign in again.";
    }
    if (reason === "terminated") {
      return "Your session has ended. Please sign in again.";
    }
    if (reason === "session-expired") {
      return "Your session has expired. Please sign in again to continue.";
    }
    return null;
  };

  const message = getMessage();

  return (
    <AnimatedCharactersLoginPage>
      <div className="relative mx-auto w-full max-w-md space-y-4">
        {message && (
          <div className="p-3 bg-amber-950/50 border border-amber-800 rounded-md">
            <p className="text-sm text-amber-200">{message}</p>
          </div>
        )}

        {mounted ? (
          <SignIn
            appearance={{
              baseTheme: dark,
              variables: {
                colorPrimary: "#5B5BFF",
                colorBackground: "#000000",
                colorInputBackground: "#18181B",
                colorInputText: "#FFFFFF",
                colorText: "#FFFFFF",
                colorTextSecondary: "#A1A1AA",
                borderRadius: "16px",
                spacingUnit: "10px",
                fontFamily: "var(--font-geist-sans)",
              },
              layout: {
                socialButtonsPlacement: "bottom",
                socialButtonsVariant: "blockButton",
              },
              elements: {
                rootBox: "w-full",
                card: "w-full shadow-none border-0 bg-black/90 backdrop-blur rounded-2xl ring-1 ring-white/10 p-6",
                headerTitle: "text-base font-semibold text-white",
                headerSubtitle: "text-sm text-gray-400",
                formFieldLabel: "hidden",
                formFieldInput:
                  "bg-zinc-900 border border-zinc-800 focus:ring-2 focus:ring-[#5B5BFF] focus:border-[#5B5BFF]",
                socialButtonsBlockButton:
                  "border border-zinc-800 bg-zinc-900 text-white hover:border-[#5B5BFF]/50 hover:bg-zinc-800 shadow-sm text-base font-semibold",
                socialButtonsBlockButton__google:
                  "bg-zinc-900 text-white border border-zinc-800 hover:border-[#5B5BFF]/50 hover:bg-zinc-800 shadow-sm text-base font-semibold",
                socialButtonsBlockButtonText: "text-white font-semibold",
                socialButtonsBlockButtonIcon: "text-white",
                formButtonPrimary:
                  "bg-gradient-to-r from-[#5B5BFF] to-[#7877FF] text-white hover:shadow-lg hover:shadow-[#5B5BFF]/20",
                footer: "!hidden",
                footerAction: "!hidden",
                footerActionText: "!hidden",
                footerActionLink: "!hidden",
                identityPreviewEditButton: "hidden",
              },
            }}
          />
        ) : (
          /* Placeholder that matches Clerk card dimensions to prevent layout shift */
          <div className="w-full h-[200px] rounded-2xl bg-black/90 ring-1 ring-white/10 animate-pulse" />
        )}
      </div>
    </AnimatedCharactersLoginPage>
  );
}

export default function SignInPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SignInContent />
    </Suspense>
  );
}
